import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Bell, AlertTriangle, Clock, X, CheckCircle2, MessageSquare } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationsContext';

export default function NotificationBell() {
  const { data, visible, dismiss, dismissAll, markRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);

  const badgeCount = visible.length;

  const toggleOpen = () => setOpen(prev => {
    const next = !prev;
    if (next) {
      markRead(); // clear reply/shared badge
      const r = btnRef.current?.getBoundingClientRect();
      if (r) setPos({ top: r.bottom + 8, left: Math.min(Math.max(r.left + r.width / 2 - 160, 8), window.innerWidth - 328) });
    }
    return next;
  });

  return (
    <div className="relative">
      {/* Bell button */}
      <button
        ref={btnRef}
        onClick={toggleOpen}
        data-testid="notifications-bell"
        className="relative w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        title="Notifications"
      >
        <Bell size={15} />
        {badgeCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center text-white"
            style={{ background: badgeCount > 0 && data.overdue_count > 0 ? '#e05a5a' : '#d4a44a' }}
          >
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </button>

      {/* Dropdown panel — portaled to body so page content can never paint above it */}
      {open && pos && createPortal(
        <>
          <div className="fixed inset-0" style={{ zIndex: 9998 }} onClick={() => setOpen(false)} />
          <div
            className="fixed rounded-2xl overflow-hidden"
            data-testid="notifications-panel"
            style={{
              zIndex: 9999,
              top: `${pos.top}px`,
              left: `${pos.left}px`,
              width: '320px',
              background: '#fff',
              border: '1px solid hsl(var(--border))',
              boxShadow: '0 12px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
              <div className="flex items-center gap-2">
                <Bell size={14} style={{ color: '#6994c6' }} />
                <span className="text-sm font-semibold text-foreground">Notifications</span>
                {badgeCount > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium text-white" style={{ background: '#e05a5a' }}>
                    {badgeCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {badgeCount > 0 && (
                  <button onClick={dismissAll} className="text-[10px] text-muted-foreground hover:text-foreground transition-colors">
                    Clear all
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground p-0.5">
                  <X size={13} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
              {visible.length === 0 ? (
                <div className="py-8 text-center">
                  <CheckCircle2 size={24} className="mx-auto mb-2" style={{ color: '#6994c6' }} />
                  <p className="text-sm font-medium text-foreground mb-1">All caught up!</p>
                  <p className="text-xs text-muted-foreground">No new replies or due tasks.</p>
                </div>
              ) : (
                <div className="py-1">
                  {visible.slice(0, 20).map(notif => (
                    <div key={notif.id} data-testid={`notification-item-${notif.id}`} className="flex items-start gap-3 px-4 py-3 hover:bg-secondary/50 group transition-colors">
                      <div
                        className="w-7 h-7 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                        style={{ background: (notif.severity === 'high' || notif.type === 'connection') ? 'rgba(224,90,90,0.10)' : ['reply', 'shared', 'team', 'mention', 'workspace_invite'].includes(notif.type) ? 'rgba(103,150,194,0.12)' : 'rgba(212,164,74,0.10)' }}
                      >
                        {(notif.severity === 'high' || notif.type === 'connection')
                          ? <AlertTriangle size={13} style={{ color: '#e05a5a' }} />
                          : ['reply', 'shared', 'team', 'mention', 'workspace_invite'].includes(notif.type)
                          ? <MessageSquare size={13} style={{ color: '#6994c6' }} />
                          : <Clock size={13} style={{ color: '#d4a44a' }} />}
                      </div>
                      <Link
                        to={notif.link || `/action-cards/${notif.action_card_id}`}
                        onClick={() => setOpen(false)}
                        className="flex-1 min-w-0"
                      >
                        <p className="text-xs font-medium text-foreground truncate">{notif.title}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{notif.message}</p>
                        {notif.action_card_title && (
                          <p className="text-[10px] text-muted-foreground truncate" style={{ color: '#6994c6' }}>
                            {notif.action_card_title}
                          </p>
                        )}
                      </Link>
                      <button
                        onClick={() => dismiss(notif.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-foreground transition-opacity shrink-0"
                      >
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {badgeCount > 0 && (
              <div style={{ borderTop: '1px solid hsl(var(--border))', padding: '10px 16px' }}>
                <Link
                  to="/tasks"
                  onClick={() => setOpen(false)}
                  className="text-xs font-medium flex items-center justify-center gap-1 transition-colors hover:opacity-80"
                  style={{ color: '#6994c6' }}
                >
                  {visible.length > 20 ? `View all tasks (${visible.length})` : 'View all tasks'}
                </Link>
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
